import { Message, TurnActivity } from '@monetize/types/app';
import findLastIndex from 'lodash/findLastIndex';
import { settleStaleActivity } from '../../services/chatActivity';
import { isDraftPreview } from '../../services/chatStream';

export type TurnItem =
  | {
      kind: 'message';
      key: string;
      message: Message;
      showAvatar: boolean;
      /** The conversation's last reply, whose actions stay visible. */
      isLatestReply: boolean;
    }
  | {
      kind: 'activity';
      key: string;
      activity: TurnActivity;
      segmentIndex: number;
      showAvatar: boolean;
      /** Work in progress right now, rather than a record of past work. */
      isLive: boolean;
    }
  /** A streaming turn with no activity record, e.g. one restored from a
   * session saved before activity was tracked. */
  | { kind: 'pending'; key: string; showAvatar: boolean };

export interface ChatTurn {
  key: string;
  /** False for messages that came before the first prompt. */
  hasPrompt: boolean;
  items: TurnItem[];
}

/** The hello message is dropped once a conversation starts under it. */
const getLeadingWelcomeId = (messages: Message[]) => {
  const visible = messages.filter(
    ({ isSubAgentMessage }) => !isSubAgentMessage,
  );
  const firstPromptIndex = visible.findIndex(({ role }) => role === 'user');
  const [first] = visible;
  return firstPromptIndex === 1 &&
    first?.role === 'assistant' &&
    first.type === 'standard'
    ? first.id
    : undefined;
};

const isVisibleReply = ({ role, isSubAgentMessage }: Message) =>
  role === 'assistant' && !isSubAgentMessage;

/**
 * The replies to show for a turn, in order, with any live draft folded in:
 * shown where it streamed until a relay follows it, then in the relay's
 * place until the relay has caught up. Error messages are never relays.
 */
const getTurnReplies = (
  turnMessages: Message[],
  isLiveTurn: boolean,
): { key: string; message: Message }[] => {
  // The latest text known to be a write-up, so the start of a later step
  // doesn't hide the one already on screen.
  const draftIndex = isLiveTurn
    ? findLastIndex(
        turnMessages,
        (message) => !!message.isSubAgentMessage && isDraftPreview(message),
      )
    : -1;
  const draft = turnMessages[draftIndex];
  if (!draft) {
    return turnMessages
      .filter(isVisibleReply)
      .map((message) => ({ key: message.id, message }));
  }
  const relayIndex = findLastIndex(
    turnMessages,
    (message, index) =>
      index > draftIndex &&
      isVisibleReply(message) &&
      message.type === 'standard',
  );
  return turnMessages.flatMap((message, index) => {
    if (index === draftIndex && relayIndex === -1) {
      return [{ key: draft.id, message: draft }];
    }
    if (!isVisibleReply(message)) {
      return [];
    }
    if (index !== relayIndex) {
      return [{ key: message.id, message }];
    }
    // Keep showing the draft until the relay has caught up with it, so a
    // near-verbatim relay doesn't make the answer shrink and retype itself.
    // Keyed by the draft so the bubble stays mounted through the handover.
    const shown =
      message.content.length >= draft.content.length
        ? message
        : { ...message, content: draft.content };
    return [{ key: draft.id, message: shown }];
  });
};

/**
 * Groups the conversation into turns -- a prompt and everything shown in
 * reply to it -- and places each stretch of the agent's work among the
 * replies it came before. Only the first assistant-side item in a turn shows
 * Monty's avatar.
 */
export const buildChatTurns = (
  messages: Message[],
  isStreaming: boolean,
): ChatTurn[] => {
  const welcomeId = getLeadingWelcomeId(messages);
  const groups: Message[][] = [];
  for (const message of messages) {
    if (message.id === welcomeId) {
      continue;
    }
    if (message.role === 'user' || groups.length === 0) {
      groups.push([message]);
    } else {
      groups[groups.length - 1].push(message);
    }
  }

  const turns = groups.map((turnMessages, groupIndex): ChatTurn => {
    const [first] = turnMessages;
    const prompt = first.role === 'user' ? first : undefined;
    const isLiveTurn =
      isStreaming && !!prompt && groupIndex === groups.length - 1;
    const replies = getTurnReplies(turnMessages, isLiveTurn);
    const activity =
      prompt?.activity &&
      (isLiveTurn ? prompt.activity : settleStaleActivity(prompt.activity));

    const items: TurnItem[] = [];
    if (prompt) {
      items.push({
        kind: 'message',
        key: prompt.id,
        message: prompt,
        showAvatar: false,
        isLatestReply: false,
      });
    }
    const pushSegmentsBefore = (replyIndex: number, isLast: boolean) => {
      if (!prompt || !activity) {
        return;
      }
      activity.segments.forEach((segment, segmentIndex) => {
        const { position, endedAt } = segment;
        if (isLast ? position < replyIndex : position !== replyIndex) {
          return;
        }
        items.push({
          kind: 'activity',
          key: `${prompt.id}:activity:${segmentIndex}`,
          activity,
          segmentIndex,
          showAvatar: false,
          isLive:
            isLiveTurn &&
            activity.status === 'running' &&
            endedAt === undefined,
        });
      });
    };

    replies.forEach((reply, replyIndex) => {
      pushSegmentsBefore(replyIndex, false);
      items.push({
        kind: 'message',
        key: reply.key,
        message: reply.message,
        showAvatar: false,
        isLatestReply: false,
      });
    });
    pushSegmentsBefore(replies.length, true);
    if (prompt && isLiveTurn && !activity) {
      items.push({
        kind: 'pending',
        key: `${prompt.id}:pending`,
        showAvatar: false,
      });
    }

    const firstAssistantItem = items.find(
      (item) => item.kind !== 'message' || item.message.role === 'assistant',
    );
    if (firstAssistantItem) {
      firstAssistantItem.showAvatar = true;
    }
    return { key: first.id, hasPrompt: !!prompt, items };
  });

  const lastItem = turns[turns.length - 1]?.items.at(-1);
  if (
    !isStreaming &&
    lastItem?.kind === 'message' &&
    lastItem.message.role === 'assistant'
  ) {
    lastItem.isLatestReply = true;
  }
  return turns;
};
